import { FinancialHealthMetrics, BehaviorMetrics } from '../../../../hooks/useExecutiveData';

const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

interface HealthProps {
  health: FinancialHealthMetrics;
}

const ProgressBar = ({ pct, color }: { pct: number; color: string }) => (
  <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
    <div className={`h-full rounded-full ${color} transition-all duration-500`} style={{ width: `${Math.min(pct, 100)}%` }} />
  </div>
);

export const FinancialHealthSection = ({ health }: HealthProps) => {
  const netColor = health.consolidatedBalance >= 0 ? 'text-emerald-400' : 'text-red-400';

  return (
    <div className="space-y-6">
      {/* Big numbers */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-5">
          <p className="text-[12px] text-emerald-400/70 mb-2">Total de Receitas</p>
          <p className="text-[26px] font-700 text-emerald-400">{fmt(health.totalIncome)}</p>
          <p className="text-[12px] text-white/30 mt-1">lançamentos pagos/recebidos</p>
        </div>
        <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-5">
          <p className="text-[12px] text-red-400/70 mb-2">Total de Despesas</p>
          <p className="text-[26px] font-700 text-red-400">{fmt(health.totalExpenses)}</p>
          <p className="text-[12px] text-white/30 mt-1">lançamentos pagos</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <p className="text-[12px] text-white/40 mb-2">Saldo Consolidado da Base</p>
          <p className={`text-[26px] font-700 ${netColor}`}>{fmt(health.consolidatedBalance)}</p>
          <p className="text-[12px] text-white/30 mt-1">receitas − despesas</p>
        </div>
      </div>

      {/* Pending overview */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: 'Total Pendente', value: fmt(health.totalPending), sub: 'aguardando liquidação', color: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/20' },
          { label: 'Total Vencido', value: fmt(health.totalOverdue), sub: 'pendências em atraso', color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20' },
          { label: 'Total Previsto Futuro', value: fmt(health.totalFuture), sub: 'a vencer', color: 'text-cyan-400', bg: 'bg-cyan-500/10 border-cyan-500/20' },
        ].map((c) => (
          <div key={c.label} className={`${c.bg} border rounded-2xl p-5`}>
            <p className="text-[12px] text-white/40 mb-2">{c.label}</p>
            <p className={`text-[22px] font-700 ${c.color}`}>{c.value}</p>
            <p className="text-[12px] text-white/30 mt-1">{c.sub}</p>
          </div>
        ))}
      </div>

      {/* User health indicators */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <h3 className="text-[14px] font-600 text-white mb-5">Saúde financeira dos usuários</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-[12px] mb-1.5">
                <span className="text-white/50">Saldo positivo</span>
                <span className="text-emerald-400 font-600">{health.pctUsersPositiveBalance}%</span>
              </div>
              <ProgressBar pct={health.pctUsersPositiveBalance} color="bg-emerald-500" />
            </div>
            <div>
              <div className="flex justify-between text-[12px] mb-1.5">
                <span className="text-white/50">Saldo negativo</span>
                <span className="text-red-400 font-600">{health.pctUsersNegativeBalance}%</span>
              </div>
              <ProgressBar pct={health.pctUsersNegativeBalance} color="bg-red-500" />
            </div>
            <div>
              <div className="flex justify-between text-[12px] mb-1.5">
                <span className="text-white/50">Com pendências vencidas</span>
                <span className="text-orange-400 font-600">{health.pctUsersWithOverdue}%</span>
              </div>
              <ProgressBar pct={health.pctUsersWithOverdue} color="bg-orange-500" />
            </div>
          </div>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <h3 className="text-[14px] font-600 text-white mb-5">Composição dos lançamentos</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-[12px] mb-1.5">
                <span className="text-white/50">Lançamentos recorrentes</span>
                <span className="text-violet-400 font-600">{health.recurringWeight}%</span>
              </div>
              <ProgressBar pct={health.recurringWeight} color="bg-violet-500" />
            </div>
            <div>
              <div className="flex justify-between text-[12px] mb-1.5">
                <span className="text-white/50">Lançamentos parcelados</span>
                <span className="text-indigo-400 font-600">{health.installmentWeight}%</span>
              </div>
              <ProgressBar pct={health.installmentWeight} color="bg-indigo-500" />
            </div>
            <div>
              <div className="flex justify-between text-[12px] mb-1.5">
                <span className="text-white/50">Lançamentos únicos</span>
                <span className="text-cyan-400 font-600">{Math.max(0, 100 - health.recurringWeight - health.installmentWeight)}%</span>
              </div>
              <ProgressBar pct={Math.max(0, 100 - health.recurringWeight - health.installmentWeight)} color="bg-cyan-500" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface BehaviorProps {
  behavior: BehaviorMetrics;
}

const fmtShort = (v: number) =>
  v >= 1000 ? `R$${(v / 1000).toFixed(1)}k` : fmt(v);

export const BehaviorSection = ({ behavior }: BehaviorProps) => {
  const maxFreq = Math.max(...behavior.topCategoriesByFrequency.map((c) => c.count), 1);
  const maxAmt = Math.max(...behavior.topCategoriesByAmount.map((c) => c.total), 1);
  const maxMonth = Math.max(...behavior.monthlyTrend.map((m) => Math.max(m.income, m.expenses)), 1);

  const fmtMonth = (key: string) => {
    const [y, m] = key.split('-');
    const months = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
    return `${months[parseInt(m, 10) - 1]}/${y.slice(2)}`;
  };

  return (
    <div className="space-y-6">
      {/* Category analysis */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <h3 className="text-[14px] font-600 text-white mb-4">Categorias mais frequentes</h3>
          {behavior.topCategoriesByFrequency.length === 0 ? (
            <p className="text-[13px] text-white/30">Sem dados no período</p>
          ) : (
            <div className="space-y-2.5">
              {behavior.topCategoriesByFrequency.map((cat) => (
                <div key={cat.name} className="flex items-center gap-3">
                  <span className="text-[12px] text-white/50 w-32 shrink-0 truncate">{cat.name}</span>
                  <div className="flex-1 h-5 bg-white/5 rounded-lg overflow-hidden">
                    <div
                      className="h-full bg-violet-500/50 rounded-lg flex items-center pl-2 transition-all duration-500"
                      style={{ width: `${Math.max((cat.count / maxFreq) * 100, cat.count > 0 ? 5 : 0)}%` }}
                    >
                      {cat.count > 0 && <span className="text-[10px] font-700 text-white whitespace-nowrap">{cat.count}</span>}
                    </div>
                  </div>
                  <span className="text-[10px] text-white/30 w-8 text-right">{cat.pct}%</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <h3 className="text-[14px] font-600 text-white mb-4">Categorias com maior gasto</h3>
          {behavior.topCategoriesByAmount.length === 0 ? (
            <p className="text-[13px] text-white/30">Sem dados no período</p>
          ) : (
            <div className="space-y-2.5">
              {behavior.topCategoriesByAmount.map((cat) => (
                <div key={cat.name} className="flex items-center gap-3">
                  <span className="text-[12px] text-white/50 w-32 shrink-0 truncate">{cat.name}</span>
                  <div className="flex-1 h-5 bg-white/5 rounded-lg overflow-hidden">
                    <div
                      className="h-full bg-rose-500/50 rounded-lg flex items-center pl-2 transition-all duration-500"
                      style={{ width: `${Math.max((cat.total / maxAmt) * 100, cat.total > 0 ? 5 : 0)}%` }}
                    >
                      {cat.total > 0 && <span className="text-[10px] font-700 text-white whitespace-nowrap">{fmtShort(cat.total)}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Account vs Card + Trend */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <h3 className="text-[14px] font-600 text-white mb-4">Conta vs Cartão</h3>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-[12px] mb-1.5">
                <span className="text-white/50">Via conta</span>
                <span className="text-cyan-400 font-600">{behavior.accountVsCardPct.account}%</span>
              </div>
              <div className="w-full h-3 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full bg-cyan-500/60 rounded-full" style={{ width: `${behavior.accountVsCardPct.account}%` }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-[12px] mb-1.5">
                <span className="text-white/50">Via cartão</span>
                <span className="text-pink-400 font-600">{behavior.accountVsCardPct.card}%</span>
              </div>
              <div className="w-full h-3 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full bg-pink-500/60 rounded-full" style={{ width: `${behavior.accountVsCardPct.card}%` }} />
              </div>
            </div>
          </div>
          {/* Pending by period */}
          <h3 className="text-[14px] font-600 text-white mb-3 mt-5">Pendências por período</h3>
          <div className="space-y-1.5">
            {behavior.pendingByPeriod.map((p) => (
              <div key={p.label} className="flex justify-between text-[12px]">
                <span className="text-white/50">{p.label}</span>
                <div className="text-right">
                  <span className="text-white font-500">{p.count} lançamentos</span>
                  {p.total > 0 && <span className="text-white/40 ml-1">({fmtShort(p.total)})</span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Monthly trend chart */}
        <div className="md:col-span-2 bg-white/5 border border-white/10 rounded-2xl p-5">
          <h3 className="text-[14px] font-600 text-white mb-5">Evolução mensal — receitas e despesas da base</h3>
          {behavior.monthlyTrend.every((m) => m.income === 0 && m.expenses === 0) ? (
            <div className="flex flex-col items-center justify-center h-[120px] text-white/30 gap-2">
              <i className="ri-bar-chart-2-line text-3xl" />
              <p className="text-[13px]">Sem dados de evolução no período</p>
            </div>
          ) : (
            <>
              <div className="flex items-end gap-2 h-[120px]">
                {behavior.monthlyTrend.map((m) => {
                  const incH = Math.max((m.income / maxMonth) * 100, m.income > 0 ? 4 : 0);
                  const expH = Math.max((m.expenses / maxMonth) * 100, m.expenses > 0 ? 4 : 0);
                  return (
                    <div key={m.month} className="flex-1 h-full flex items-end gap-0.5 group relative">
                      <div
                        className="flex-1 bg-emerald-500/60 rounded-t transition-all duration-500"
                        style={{ height: `${incH}%`, minHeight: m.income > 0 ? '4px' : '0' }}
                        title={`Receitas: ${fmt(m.income)}`}
                      />
                      <div
                        className="flex-1 bg-red-500/60 rounded-t transition-all duration-500"
                        style={{ height: `${expH}%`, minHeight: m.expenses > 0 ? '4px' : '0' }}
                        title={`Despesas: ${fmt(m.expenses)}`}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="flex mt-2">
                {behavior.monthlyTrend.map((m) => (
                  <span key={m.month} className="flex-1 text-center text-[10px] text-white/30">{fmtMonth(m.month)}</span>
                ))}
              </div>
              <div className="flex items-center gap-4 mt-3">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-sm bg-emerald-500/60" />
                  <span className="text-[11px] text-white/40">Receitas</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-sm bg-red-500/60" />
                  <span className="text-[11px] text-white/40">Despesas</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
